import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Plus, Minus, Zap, Target, Circle } from 'lucide-react';

// Custom Rugby Ball SVG icon component
const RugbyBallIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <ellipse cx="12" cy="12" rx="10" ry="6" transform="rotate(45 12 12)" />
    <path d="M7 7l10 10" />
    <path d="M8.5 12l3-3" />
    <path d="M12 15.5l3-3" />
  </svg>
);

const SPORT_CONFIG = {
  soccer: {
    name: 'Soccer',
    scoreOptions: [{ label: 'Goal', value: 1, type: 'generic', event: 'GOAL' }],
    hasCombinedScore: false
  },
  rugby: {
    name: 'Rugby',
    scoreOptions: [
      { label: 'Try', value: 5, type: 'generic', event: 'TRY' },
      { label: 'Conversion', value: 2, type: 'generic', event: null },
      { label: 'Penalty', value: 3, type: 'generic', event: null },
      { label: 'Drop Goal', value: 3, type: 'generic', event: null }
    ],
    hasCombinedScore: false
  },
  gaelic_football: {
    name: 'Gaelic Football',
    scoreOptions: [
      { label: 'Goal (3)', value: 3, type: 'goal', event: 'GOAL' },
      { label: 'Point (1)', value: 1, type: 'point', event: 'POINT' }
    ],
    hasCombinedScore: true
  },
  hurling: {
    name: 'Hurling / Camogie',
    scoreOptions: [
      { label: 'Goal (3)', value: 3, type: 'goal', event: 'GOAL' },
      { label: 'Point (1)', value: 1, type: 'point', event: 'POINT' }
    ],
    hasCombinedScore: true
  }
};

const ScoreControls = ({ 
  game, 
  syncing, 
  onScore, 
  onTriggerEvent,
  onTriggerGoalGraphic
}) => {
  const sportConfig = SPORT_CONFIG[game.sport];
  const [showGoalDialog, setShowGoalDialog] = useState(false);
  const [pendingGoal, setPendingGoal] = useState(null); // { team: 1|2, scoreType, value }
  const [selectedScorer, setSelectedScorer] = useState('');
  const [manualScorerName, setManualScorerName] = useState('');
  const [manualScorerNumber, setManualScorerNumber] = useState('');
  
  // Get lineup players for a team
  const getTeamPlayers = (teamNum) => {
    const lineup = game[`lineup_team${teamNum}`];
    if (!lineup?.submitted) return [];
    const starting = lineup.starting || [];
    const subs = lineup.substitutes || [];
    return [...starting, ...subs];
  };
  
  // Calculate current minute from timer
  const getCurrentMinute = () => {
    const currentTime = game.current_time || 0;
    return Math.floor(currentTime / 60) + 1;
  };
  
  const formatGAAScore = (team) => {
    if (sportConfig?.hasCombinedScore) {
      return `${team.goals || 0}-${team.points || 0}`;
    }
    return team.score || 0;
  };

  const handleGoalClick = (teamNum, scoreType, value, event) => {
    // Add the score first
    onScore(teamNum, scoreType, value);
    
    // Skip scorer dialog for GAA sports (scoring is too frequent)
    const isGAA = game.sport === 'gaelic_football' || game.sport === 'hurling';
    if (isGAA) {
      // Just trigger the event without showing the dialog
      if (event) {
        onTriggerEvent(event, teamNum);
      }
      return;
    }
    
    // For other sports, show the goal dialog to select/enter scorer
    setPendingGoal({ team: teamNum, event });
    setSelectedScorer('');
    setManualScorerName('');
    setManualScorerNumber('');
    setShowGoalDialog(true);
  };

  const handleScorerSelect = () => {
    if (!pendingGoal) return;
    
    const players = getTeamPlayers(pendingGoal.team);
    
    // Check if using dropdown selection or manual entry
    if (selectedScorer && players.length > 0) {
      const scorer = players.find(p => p.id === selectedScorer);
      if (scorer && onTriggerGoalGraphic) {
        onTriggerGoalGraphic(pendingGoal.team, {
          name: scorer.name,
          number: String(scorer.number || ''),
          minute: getCurrentMinute()
        });
      }
    } else if (manualScorerName.trim() && onTriggerGoalGraphic) {
      // Manual entry mode
      onTriggerGoalGraphic(pendingGoal.team, {
        name: manualScorerName.trim(),
        number: manualScorerNumber.trim() || '',
        minute: getCurrentMinute()
      });
    } else if (pendingGoal.event) {
      onTriggerEvent(pendingGoal.event, pendingGoal.team);
    }
    
    setShowGoalDialog(false);
    setPendingGoal(null);
    setSelectedScorer('');
    setManualScorerName('');
    setManualScorerNumber('');
  };

  const handleSkipScorer = () => {
    if (pendingGoal?.event) {
      onTriggerEvent(pendingGoal.event, pendingGoal.team);
    }
    setShowGoalDialog(false);
    setPendingGoal(null);
    setSelectedScorer('');
    setManualScorerName('');
    setManualScorerNumber('');
  };

  const pendingTeamName = pendingGoal ? game[`team${pendingGoal.team}`]?.name : '';
  const pendingPlayers = pendingGoal ? getTeamPlayers(pendingGoal.team) : [];
  const hasPlayers = pendingPlayers.length > 0;
  const canSubmit = hasPlayers ? !!selectedScorer : !!manualScorerName.trim();

  return (
    <>
      <div className="space-y-4">
        {/* Team 1 */}
        <Card className="bg-[#1E293B] border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white font-heading text-xl flex items-center justify-between">
              <span>{game.team1.name}</span>
              <span className="font-mono text-3xl text-[#D4E5ED]">{formatGAAScore(game.team1)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {sportConfig?.scoreOptions.map((option) => (
                <div key={option.label} className="flex flex-col gap-1">
                  <span className="text-slate-400 text-xs font-body text-center">{option.label}</span>
                  <div className="flex gap-1">
                    <Button
                      data-testid={`team1-${option.type}-minus`}
                      onClick={() => onScore(1, option.type, -option.value)}
                      disabled={syncing}
                      className="flex-1 h-12 bg-slate-700 hover:bg-slate-600 text-white touch-target"
                    >
                      <Minus className="w-5 h-5" />
                    </Button>
                    <Button
                      data-testid={`team1-${option.type}-plus`}
                      onClick={() => handleGoalClick(1, option.type, option.value, option.event)}
                      disabled={syncing}
                      className="flex-1 h-12 bg-[#22C55E] hover:bg-[#16A34A] text-white touch-target"
                    >
                      <Plus className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Team 2 */}
        <Card className="bg-[#1E293B] border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white font-heading text-xl flex items-center justify-between">
              <span>{game.team2.name}</span>
              <span className="font-mono text-3xl text-[#D4E5ED]">{formatGAAScore(game.team2)}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {sportConfig?.scoreOptions.map((option) => (
                <div key={option.label} className="flex flex-col gap-1">
                  <span className="text-slate-400 text-xs font-body text-center">{option.label}</span>
                  <div className="flex gap-1">
                    <Button
                      data-testid={`team2-${option.type}-minus`}
                      onClick={() => onScore(2, option.type, -option.value)}
                      disabled={syncing}
                      className="flex-1 h-12 bg-slate-700 hover:bg-slate-600 text-white touch-target"
                    >
                      <Minus className="w-5 h-5" />
                    </Button>
                    <Button
                      data-testid={`team2-${option.type}-plus`}
                      onClick={() => handleGoalClick(2, option.type, option.value, option.event)}
                      disabled={syncing}
                      className="flex-1 h-12 bg-[#22C55E] hover:bg-[#16A34A] text-white touch-target"
                    >
                      <Plus className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Goal/Try Scorer Dialog */}
      <Dialog open={showGoalDialog} onOpenChange={setShowGoalDialog}>
        <DialogContent className="bg-[#1E293B] border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {game.sport === 'rugby' ? (
                <RugbyBallIcon className="w-5 h-5 text-green-400" />
              ) : (
                <Target className="w-5 h-5 text-green-400" />
              )}
              {game.sport === 'rugby' ? 'Try Scored!' : 'Goal Scored!'} - {pendingTeamName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-slate-400 text-sm font-body">
              {hasPlayers 
                ? `Select the ${game.sport === 'rugby' ? 'try' : 'goal'} scorer to display on the graphics overlay.`
                : `Enter the ${game.sport === 'rugby' ? 'try' : 'goal'} scorer details to display on the graphics overlay.`}
            </p>
            
            {hasPlayers ? (
              <div>
                <label className="text-slate-300 text-sm font-body block mb-2">
                  {game.sport === 'rugby' ? 'Try Scorer' : 'Goal Scorer'}
                </label>
                <Select value={selectedScorer} onValueChange={setSelectedScorer}>
                  <SelectTrigger className="bg-[#0F172A] border-slate-600 text-white">
                    <SelectValue placeholder="Select player..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-slate-600 max-h-60">
                    {pendingPlayers.map((player) => (
                      <SelectItem key={player.id} value={player.id}>
                        #{player.number} - {player.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-slate-300 text-sm font-body block mb-2">Scorer Name *</label>
                  <Input
                    value={manualScorerName}
                    onChange={(e) => setManualScorerName(e.target.value)}
                    placeholder="Enter player name..."
                    className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <label className="text-slate-300 text-sm font-body block mb-2">Jersey Number (optional)</label>
                  <Input
                    value={manualScorerNumber}
                    onChange={(e) => setManualScorerNumber(e.target.value)}
                    placeholder="e.g. 10"
                    className="bg-[#0F172A] border-slate-600 text-white placeholder:text-slate-500 w-24"
                  />
                </div>
              </div>
            )}

            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
              <p className="text-slate-400 text-xs">Current Minute</p>
              <p className="text-2xl font-bold text-white">{getCurrentMinute()}&apos;</p>
            </div>
            
            <div className="flex gap-2">
              <Button
                onClick={handleSkipScorer}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Skip
              </Button>
              <Button
                onClick={handleScorerSelect}
                disabled={!canSubmit}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <Zap className="w-4 h-4 mr-2" />
                {game.sport === 'rugby' ? 'Show Try Graphic' : 'Show Goal Graphic'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ScoreControls;
